import { computed, inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  finalize,
  merge,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';

import { HttpErrorResponse } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from './auth.service';
import { LocalStorageService } from './local-storage.service';
import { LocalStorageKeyEnum } from '../enums';
import { PlayerAddress, User } from '@models';
import { ApiService } from './api-service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly localStorageService = inject(LocalStorageService);
  private readonly authService = inject(AuthService);
  private readonly apiService = inject(ApiService);
  isUserLoaded$ = new BehaviorSubject<boolean>(false);
  reloadUser$ = new Subject<void>();

  user = toSignal(
    merge(this.authService.tokenChanges$, this.reloadUser$).pipe(
      startWith(null),
      debounceTime(10),
      switchMap(() => (this.authService.token ? this.getUserRequest() : of(null))),
    ),
    { initialValue: null },
  );
  isUserLoaded = toSignal(this.isUserLoaded$, { initialValue: false });

  // The casino may send an empty first or last name; show it empty, never a placeholder.
  firstName = computed(() => this.user()?.firstName?.trim() ?? '');
  middleName = computed(() => this.user()?.middleName?.trim() ?? '');
  lastName = computed(() => this.user()?.lastName?.trim() ?? '');
  greetingName = computed(() => this.firstName() || this.lastName());
  emailAddress = computed(() => this.user()?.email ?? '');

  // Null when the casino has no home address (all fields empty).
  address = computed<PlayerAddress | null>(() => {
    const address = this.user()?.address;
    return address && Object.values(address).some((value) => !!value?.trim()) ? address : null;
  });

  // Balance is in whole Shopping Dollars, 1 Shopping Dollar = 1 USD.
  balance = computed(() => this.user()?.balance ?? null);
  points = computed(() => {
    const value = Math.floor(Number.parseFloat((this.balance()?.accountBalance ?? '').replace(/[^0-9.-]/g, '')));
    return Number.isFinite(value) && value > 0 ? value : 0;
  });
  canRedeemPoints = computed(() => this.points() > 0);

  // Never rethrow: an errored stream would kill the `user` toSignal, and the
  // signal would then throw on every read (i.e. on every change detection).
  private getUserRequest(): Observable<User | null> {
    return this.apiService.get<User>('user').pipe(
      finalize(() => this.isUserLoaded$.next(true)),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 403) {
          this.authService.logout();
        }
        return of(null);
      }),
    );
  }

  updateUserInfo(): void {
    this.reloadUser$.next();
  }

  setShoppingJourneyId(id: string): void {
    this.localStorageService.setItem(LocalStorageKeyEnum.SHOPPING_JOURNEY_ID, id);
  }
}
